import { RecordListScroll } from '@liza/ui/components/RecordListScroll';
import { useInfiniteRecordList } from '@liza/ui/hooks/useInfiniteRecordList';
import { useTRPC, useTRPCClient } from '@/utils/trpc';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { AddLabResultForm, type AddLabResultFormValues } from '@liza/ui/components/records/AddLabResultForm';
import { LabsList } from '@liza/ui/components/records/LabsList';
import {
  type RecordsListFilters,
  defaultFilters,
  RecordsListControls,
} from '@liza/ui/components/records/RecordsListControls';
import type { ValueSetCode } from '@liza/ui/components/records/ValueSetCombobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@liza/ui/components/ui/dialog';
import { useCuratedLabItems } from '@liza/ui/hooks/useCuratedLabItems';
import { useRecordPromotionStatus } from '@liza/ui/hooks/useRecordPromotionStatus';
import { classifyObservation, getObservationIdentifier, shouldExcludeObservation } from '@liza/ui/utils/observation';
import { formatCodeableConcept } from '@medplum/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { match } from 'ts-pattern';
import { useDebouncedCallback } from 'use-debounce';
import type { DetailPanelView } from '../types';
import { parseLabLookupProps } from './labsLookup';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  onViewChange: (view: DetailPanelView | null) => void;
  patient: Patient;
  selectedLabIdentifier?: string;
};

type ObservationValueFields =
  | { valueQuantity: { value: number; unit: string; system: string; code: string } }
  | { valueCodeableConcept: { text: string } }
  | { valueString: string };

const buildObservationValueFields = (values: AddLabResultFormValues): ObservationValueFields =>
  match(values)
    .with({ scaleType: 'Qn' }, (v) => ({
      // FHIR `Quantity.unit` is human-readable display and `.code` is the UCUM code; they're
      // usually distinct (compare `Vitals.tsx`). Here both come from LOINC's `EXAMPLE_UCUM_UNITS`,
      // which is defined as a UCUM symbol — and the form makes the unit input read-only and
      // prop-driven (#2988), so the user can't introduce a display-only string. They coincide
      // intentionally. If a future data source surfaces both display and code separately, split
      // them at the form-values level (see `Vitals.tsx` for the pattern).
      valueQuantity: {
        value: Number(v.value),
        unit: v.unit,
        system: 'http://unitsofmeasure.org',
        code: v.unit,
      },
    }))
    .with({ scaleType: 'Ord' }, (v) => ({ valueCodeableConcept: { text: v.value } }))
    .with({ scaleType: 'Nar' }, (v) => ({ valueString: v.value }))
    .exhaustive();

export const Labs = ({ onViewChange, patient, selectedLabIdentifier }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const handleFiltersChange = useDebouncedCallback(setFilters, 300);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedLab, setSelectedLab] = useState<ValueSetCode | undefined>(undefined);

  const closeAddDialog = useCallback((): void => {
    setIsAddOpen(false);
    setSelectedLab(undefined);
  }, []);

  const listOptions = {
    ownership: filters.ownership,
    searchParams: { 'code:text': filters.searchQuery },
    status: filters.status,
  };

  const {
    items: observations,
    query,
    bottomSentinelRef,
  } = useInfiniteRecordList({
    queryKey: trpc.patient.observation.list.queryKey({
      category: 'labs',
      medplumPatientId: patient.medplumId,
      options: listOptions,
    }),
    queryFn: async ({ offset, count }) => {
      const res = await trpcClient.patient.observation.list.query({
        category: 'labs',
        medplumPatientId: patient.medplumId,
        options: listOptions,
        offset,
        count,
      });
      return { items: res.observations, hasMore: res.hasMore };
    },
  });

  // Client-side filter: keep only leaf-level result observations (not panel headers etc.). The
  // server already scopes to category=labs, so misses should be infrequent — but because the
  // infinite-scroll sentinel keys off rendered rows, a page that filters down to few rows will
  // eagerly fetch the next page until the viewport fills. If that drives network spikes, move the
  // filter server-side (see #4483).
  const filteredObservations = useMemo(
    () =>
      observations.filter(
        (obs) => classifyObservation(obs) === 'result' && !shouldExcludeObservation(formatCodeableConcept(obs.code)),
      ),
    [observations],
  );

  const labType = useCuratedLabItems();

  const lookupQuery = useQuery({
    ...trpc.codeSystem.lookup.queryOptions({
      system: selectedLab?.system ?? '',
      code: selectedLab?.code ?? '',
      properties: ['EXAMPLE_UCUM_UNITS', 'SCALE_TYP', 'ANSWER_LIST'],
    }),
    enabled: Boolean(selectedLab?.system && selectedLab.code),
  });

  const lookupProps = selectedLab
    ? parseLabLookupProps(lookupQuery.data?.properties)
    : { scaleType: 'Qn' as const, unit: undefined, ordinalChoices: undefined };

  const { mutateAsync: createObservation, isPending: isCreating } = useMutation(
    trpc.patient.observation.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.observation.list.queryKey({
            category: 'labs',
            medplumPatientId: patient.medplumId,
          }),
        });
      },
    }),
  );

  const handleCreateLabResult = useCallback(
    async (values: AddLabResultFormValues): Promise<void> => {
      try {
        await createObservation({
          resource: {
            resourceType: 'Observation',
            status: 'final',
            category: [
              {
                coding: [
                  {
                    system: 'http://terminology.hl7.org/CodeSystem/observation-category',
                    code: 'laboratory',
                  },
                ],
              },
            ],
            code: {
              coding: [
                {
                  system: values.labType.system,
                  code: values.labType.code,
                  display: values.labType.display,
                },
              ],
              text: values.labType.display ?? values.labType.code,
            },
            subject: { reference: `Patient/${patient.medplumId}` },
            effectiveDateTime: new Date(values.effectiveDateTime).toISOString(),
            issued: new Date().toISOString(),
            ...buildObservationValueFields(values),
            note: values.note ? [{ text: values.note }] : undefined,
          },
        });
        toast.success(t('labResultAdded'));
        closeAddDialog();
      } catch (error) {
        toast.error(`${t('addLabResultFailed')}: ${error instanceof Error ? error.message : ''}`);
        throw error;
      }
    },
    [closeAddDialog, createObservation, patient.medplumId, t],
  );

  const { promote, getPromotionStatus } = useRecordPromotionStatus();

  const handlePromotionClick = useCallback(
    (labIdentifier: string): void => {
      const group = filteredObservations.filter((obs) => getObservationIdentifier(obs) === labIdentifier);
      const latest = [...group].sort(
        (a, b) => new Date(b.effectiveDateTime || '').getTime() - new Date(a.effectiveDateTime || '').getTime(),
      )[0];
      if (!latest) return;

      void promote(labIdentifier, async () =>
        createObservation({
          resource: {
            ...latest,
            id: undefined,
            subject: { reference: `Patient/${patient.medplumId}` },
            issued: new Date().toISOString(),
          },
        }),
      );
    },
    [createObservation, filteredObservations, patient, promote],
  );

  return (
    <div className="flex flex-col gap-2 p-2">
      <RecordsListControls
        title={t('labs')}
        searchPlaceholder={t('searchLabs')}
        onFiltersChange={handleFiltersChange}
        onAdd={() => setIsAddOpen(true)}
        addLabel={t('addLabResult')}
      />
      <Dialog
        open={isAddOpen}
        onOpenChange={(open) => {
          if (!open) closeAddDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('addLabResult')}</DialogTitle>
          </DialogHeader>
          <AddLabResultForm
            isCreating={isCreating}
            onClose={closeAddDialog}
            onCreate={handleCreateLabResult}
            labType={labType}
            onLabTypeChange={setSelectedLab}
            scaleType={lookupProps.scaleType}
            isScaleLoading={lookupQuery.isFetching}
            unit={lookupProps.unit}
            isUnitLoading={lookupQuery.isFetching}
            ordinalChoices={lookupProps.ordinalChoices}
          />
        </DialogContent>
      </Dialog>
      <RecordListScroll
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isFetchingNextPage={query.isFetchingNextPage}
        bottomSentinelRef={bottomSentinelRef}
      >
        <LabsList
          labs={filteredObservations}
          onSelectLab={(labIdentifier) => onViewChange({ type: 'lab-detail', labIdentifier })}
          selectedLabIdentifier={selectedLabIdentifier}
          onPromotionClick={filters.ownership === 'external' ? handlePromotionClick : undefined}
          getPromotionStatus={getPromotionStatus}
        />
      </RecordListScroll>
    </div>
  );
};
